import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import Layout from '../components/Layout';
import { authAPI, appAPI, pomodoroAPI, Application, CreateApplicationRequest, UpdateApplicationRequest, MockPomodoroRequest } from '../services/api';
import './Dashboard.css';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showToast } = useToast();
  const [isResendingEmail, setIsResendingEmail] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingApp, setEditingApp] = useState<Application | null>(null);
  const [newToken, setNewToken] = useState<{ appId: number; token: string } | null>(null);
  const [newlyCreatedAppToken, setNewlyCreatedAppToken] = useState<{ appId: number; token: string } | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });

  // Mock pomodoro states
  const [showMockDialog, setShowMockDialog] = useState(false);
  const [mockDescription, setMockDescription] = useState('');
  const [selectedAppForMock, setSelectedAppForMock] = useState<Application | null>(null);
  const [isSubmittingMock, setIsSubmittingMock] = useState(false);

  
  const handleResendVerification = async () => {
    if (!user?.email) {
      showToast('No email address found', 'error');
      return;
    }

    setIsResendingEmail(true);
    try {
      await authAPI.resendVerification(user.email);
      showToast('Verification email sent successfully! Please check your inbox.', 'success');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to send verification email';
      showToast(errorMessage, 'error');
    } finally {
      setIsResendingEmail(false);
    }
  };

  // Application management functions
  const fetchApplications = async () => {
    try {
      setIsLoading(true);
      const response = await appAPI.getApplications();
      setApplications(response || []);
    } catch (error) {
      showToast('Failed to fetch applications', 'error');
      setApplications([]); // Ensure we always have an array
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateApp = async () => {
    if (!formData.name.trim()) {
      showToast('Application name is required', 'error');
      return;
    }

    try {
      const createData: CreateApplicationRequest = {
        name: formData.name,
        description: formData.description || '',
        scopes: 'read,write' // Default scopes
      };

      const newApp = await appAPI.createApplication(createData);
      // Refetch data to ensure we have the latest information
      await fetchApplications();

      // Store the newly created app token if available
      if (newApp.app_token) {
        setNewlyCreatedAppToken({ appId: newApp.id, token: newApp.app_token });
      }

      setFormData({ name: '', description: '' });
      setShowCreateForm(false);
      showToast('Application created successfully!', 'success');
    } catch (error) {
      showToast('Failed to create application', 'error');
    }
  };

  const handleUpdateApp = async () => {
    if (!editingApp || !formData.name.trim()) {
      showToast('Application name is required', 'error');
      return;
    }

    try {
      const updateData: UpdateApplicationRequest = {
        name: formData.name,
        description: formData.description || '',
        scopes: 'read,write' // Keep default scopes
      };

      const updatedApp = await appAPI.updateApplication(editingApp.id, updateData);

      // Refetch data to ensure we have the latest information
      await fetchApplications();
      setFormData({ name: '', description: '' });
      setEditingApp(null);
      setShowCreateForm(false);
      showToast('Application updated successfully!', 'success');
    } catch (error) {
      showToast('Failed to update application', 'error');
    }
  };

  const handleDeleteApp = async (appId: number) => {
    if (!window.confirm('Are you sure you want to delete this application?')) {
      return;
    }

    try {
      await appAPI.deleteApplication(appId);
      // Refetch data to ensure we have the latest information
      await fetchApplications();
      showToast('Application deleted successfully!', 'success');
    } catch (error) {
      showToast('Failed to delete application', 'error');
    }
  };

  const handleEditApp = (app: Application) => {
    setEditingApp(app);
    setFormData({ name: app.name, description: app.description });
    setShowCreateForm(true);
  };

  const handleCancelForm = () => {
    setFormData({ name: '', description: '' });
    setEditingApp(null);
    setShowCreateForm(false);
  };

  const copyApiKey = (appId: string) => {
    navigator.clipboard.writeText(appId);
    showToast('App ID copied to clipboard!', 'success');
  };

  const handleViewAppDetails = (appId: number) => {
    navigate(`/dashboard/applications/${appId}`);
  };

  const handleRegenerateToken = async (appId: number) => {
    if (!window.confirm('Are you sure you want to regenerate the app token? This will invalidate the current token.')) {
      return;
    }

    try {
      const updatedApp = await appAPI.regenerateToken(appId);

      const updatedApps = (applications || []).map(app =>
        app.id === appId ? updatedApp : app
      );

      setApplications(updatedApps);

      // Store the new token to display on the page
      if (updatedApp.app_token) {
        setNewToken({ appId, token: updatedApp.app_token });
        showToast('App token regenerated successfully!', 'success');
      } else {
        showToast('App token regenerated but failed to retrieve token', 'error');
      }
    } catch (error) {
      showToast('Failed to regenerate app token', 'error');
    }
  };

  const copyNewToken = (token: string) => {
    navigator.clipboard.writeText(token);
    showToast('New app token copied to clipboard!', 'success');
  };

  const closeTokenDisplay = () => {
    setNewToken(null);
  };

  // Mock pomodoro functions
  const handleMockPomodoro = (app: Application) => {
    setSelectedAppForMock(app);
    setMockDescription('');
    setShowMockDialog(true);
  };

  const handleCancelMock = () => {
    setShowMockDialog(false);
    setMockDescription('');
    setSelectedAppForMock(null);
  };

  const handleSubmitMock = async () => {
    if (!mockDescription.trim()) {
      showToast('Please enter a description for the pomodoro session', 'error');
      return;
    }

    if (!selectedAppForMock) {
      showToast('No application selected', 'error');
      return;
    }

    setIsSubmittingMock(true);
    try {
      await pomodoroAPI.mockPomodoro({
        application_id: selectedAppForMock.id,
        description: mockDescription.trim()
      });

      showToast(`Mock pomodoro created successfully for ${selectedAppForMock.name}!`, 'success');
      setShowMockDialog(false);
      setMockDescription('');
      setSelectedAppForMock(null);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Failed to create mock pomodoro';
      showToast(errorMessage, 'error');
    } finally {
      setIsSubmittingMock(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  return (
    <Layout>
      <div className="dashboard-container">
        <main className="dashboard-main">
        <div className="welcome-card">
          <h2 className="tomast-title">Welcome to Pomodoro Timer Dashboard</h2>
          <p>This is your personal workspace for tracking and managing your pomodoro sessions.</p>

          <div className="user-details">
            <h3>Your Profile Information</h3>
            <div className="details-grid">
              <div className="detail-item">
                <label>Username:</label>
                <span>{user?.username}</span>
              </div>
              <div className="detail-item">
                <label>Email:</label>
                <span>{user?.email}</span>
              </div>
              <div className="detail-item">
                <label>Full Name:</label>
                <span>{user?.first_name} {user?.last_name}</span>
              </div>
              <div className="detail-item">
                <label>Account Status:</label>
                <span className={`status ${user?.is_active ? 'active' : 'inactive'}`}>
                  {user?.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="detail-item">
                <label>Email Verified:</label>
                <div className="email-verification-status">
                  <span className={`status ${user?.is_verified ? 'verified' : 'unverified'}`}>
                    {user?.is_verified ? 'Verified' : 'Not Verified'}
                  </span>
                  {!user?.is_verified && (
                    <button
                      onClick={handleResendVerification}
                      disabled={isResendingEmail}
                      className="resend-email-button"
                      title="Resend verification email"
                    >
                      {isResendingEmail ? 'Sending...' : 'Resend Email'}
                    </button>
                  )}
                </div>
              </div>
              <div className="detail-item">
                <label>Member Since:</label>
                <span>{user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="app-management">
            <h3>Application Management</h3>
            <p>Manage your applications and API keys for accessing Tomast services.</p>

            <div className="app-actions">
              <button
                onClick={() => setShowCreateForm(true)}
                className="create-app-button"
              >
                Create New Application
              </button>
            </div>

            {showCreateForm && (
              <div className="app-form-card">
                <h4>{editingApp ? 'Edit Application' : 'Create New Application'}</h4>
                <form onSubmit={(e) => {
                  e.preventDefault();
                  editingApp ? handleUpdateApp() : handleCreateApp();
                }}>
                  <div className="form-group">
                    <label htmlFor="app-name">Application Name *</label>
                    <input
                      id="app-name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Enter application name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="app-description">Description</label>
                    <textarea
                      id="app-description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Enter application description"
                      rows={3}
                    />
                  </div>
                  <div className="form-actions">
                    <button type="submit" className="submit-button">
                      {editingApp ? 'Update Application' : 'Create Application'}
                    </button>
                    <button type="button" onClick={handleCancelForm} className="cancel-button">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="applications-list">
              {isLoading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading applications...</p>
                </div>
              ) : !applications || applications.length === 0 ? (
                <div className="empty-state">
                  <h4>No Applications Yet</h4>
                  <p>Create your first application to start using Tomast API services.</p>
                </div>
              ) : (
                <div className="app-grid">
                  {applications.map((app) => (
                    <div key={app.id} className="app-card clickable" onClick={() => handleViewAppDetails(app.id)}>
                      <div className="app-header">
                        <h4>{app.name}</h4>
                        <div className="app-actions-menu" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => handleMockPomodoro(app)}
                            className="action-button mock-button"
                            title="Create mock pomodoro"
                          >
                            Mock
                          </button>
                          <button
                            onClick={() => handleEditApp(app)}
                            className="action-button edit-button"
                            title="Edit application"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleRegenerateToken(app.id)}
                            className="action-button regenerate-button"
                            title="Regenerate app token"
                          >
                            Regenerate
                          </button>
                          <button
                            onClick={() => handleDeleteApp(app.id)}
                            className="action-button delete-button"
                            title="Delete application"
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      <p className="app-description">{app.description || 'No description provided'}</p>
                      <div className="app-details">
                        <div className="api-key-section">
                          <label>App ID:</label>
                          <div className="api-key-container">
                            <code className="api-key">{app.app_id}</code>
                            <button
                              onClick={() => copyApiKey(app.app_id)}
                              className="copy-button"
                              title="Copy App ID"
                            >
                              Copy
                            </button>
                          </div>
                        </div>

                        {/* Display token for newly created or regenerated apps */}
                        {(newToken?.appId === app.id || newlyCreatedAppToken?.appId === app.id) && (
                          <div className="app-token-section">
                            <label>
                              {newToken?.appId === app.id ? '🔄 Regenerated App Token:' : '🆕 New App Token:'}
                            </label>
                            <div className="token-display-inline">
                              <div className="token-value-container">
                                <code className="token-value">
                                  {newToken?.appId === app.id ? newToken.token : newlyCreatedAppToken?.token}
                                </code>
                                <button
                                  onClick={() => {
                                    const token = newToken?.appId === app.id ? newToken.token : newlyCreatedAppToken?.token;
                                    if (token) copyNewToken(token);
                                  }}
                                  className="copy-button"
                                  title="Copy token"
                                >
                                  Copy
                                </button>
                                <button
                                  onClick={() => {
                                    if (newToken?.appId === app.id) {
                                      setNewToken(null);
                                    } else if (newlyCreatedAppToken?.appId === app.id) {
                                      setNewlyCreatedAppToken(null);
                                    }
                                  }}
                                  className="close-token-button"
                                  title="Dismiss token"
                                >
                                  ×
                                </button>
                              </div>
                              <p className="token-warning-inline">
                                <strong>Save this token securely!</strong> It won't be shown again.
                              </p>
                            </div>
                          </div>
                        )}

                        <div className="app-meta">
                          <span>Created: {new Date(app.created_at).toLocaleDateString()}</span>
                          {app.updated_at !== app.created_at && (
                            <span>Updated: {new Date(app.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="next-steps">
            <h3>Getting Started</h3>
            <div className="steps-grid">
              <div className="step-card">
                <h4>🍅 Start a Pomodoro Session</h4>
                <p>Begin tracking your focus time with our timer.</p>
              </div>
              <div className="step-card">
                <h4>📊 View Your Statistics</h4>
                <p>Analyze your productivity patterns and trends.</p>
              </div>
              <div className="step-card">
                <h4>⚙️ Configure Settings</h4>
                <p>Customize your pomodoro experience.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Mock Pomodoro Dialog */}
        {showMockDialog && (
          <div className="dialog-overlay">
            <div className="dialog-card">
              <div className="dialog-header">
                <h3>Create Mock Pomodoro</h3>
                <button
                  className="dialog-close-button"
                  onClick={handleCancelMock}
                  disabled={isSubmittingMock}
                >
                  ×
                </button>
              </div>
              <div className="dialog-content">
                <p>Create a mock pomodoro session for <strong>{selectedAppForMock?.name}</strong></p>
                <p className="dialog-description">
                  This will create a pomodoro session that started 25 minutes ago and ended just now.
                </p>
                <div className="form-group">
                  <label htmlFor="mock-description">Description *</label>
                  <textarea
                    id="mock-description"
                    value={mockDescription}
                    onChange={(e) => setMockDescription(e.target.value)}
                    placeholder="What did you work on during this pomodoro session?"
                    rows={3}
                    disabled={isSubmittingMock}
                  />
                </div>
              </div>
              <div className="dialog-actions">
                <button
                  onClick={handleCancelMock}
                  disabled={isSubmittingMock}
                  className="cancel-button"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSubmitMock}
                  disabled={isSubmittingMock || !mockDescription.trim()}
                  className="submit-button"
                >
                  {isSubmittingMock ? 'Creating...' : 'Create Mock Pomodoro'}
                </button>
              </div>
            </div>
          </div>
        )}
        </main>
      </div>
    </Layout>
  );
};

export default Dashboard;